import AddIcon from "@mui/icons-material/Add"
import CloseIcon from "@mui/icons-material/Close"
import { Box, Drawer, IconButton, Typography } from "@mui/material"
import { extraColors } from "../../utils/theme"
import { TOOLBAR_HEIGHT } from "../DrawerNav"
import { useBioMeshUIState } from "./BioMeshUIStateContext"
import { NodeAccordion } from "./NodeAccordion"
import { useNodeDrawerData } from "./useNodeDrawerData"

export const DRAWER_WIDTH = 520

interface NodeDrawerProps {
  nodeIds: string[]
  onClose: () => void
  title?: string
  unmapped?: boolean
  onPlaceOnMap?: (nodeId: string) => void
  onCreateUnmapped?: () => void
}

export const NodeDrawerView = ({
  nodeIds,
  onClose,
  title = "Node Details",
  unmapped,
  onPlaceOnMap,
  onCreateUnmapped,
}: NodeDrawerProps) => {
  const open = nodeIds.length > 0
  const { filters } = useBioMeshUIState()

  const { nodeOptions, sensorsByNode, operationMap, unitMap, contactMap } =
    useNodeDrawerData(nodeIds, filters)

  return (
    <Drawer
      anchor="right"
      variant="persistent"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            top: TOOLBAR_HEIGHT,
            height: `calc(100% - ${TOOLBAR_HEIGHT}px)`,
            width: DRAWER_WIDTH,
            bgcolor: extraColors.dark,
            borderLeft: `1px solid ${extraColors.translucentWhite}`,
            p: 3,
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="h3Bold">{title}</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {onCreateUnmapped && (
            <IconButton
              onClick={onCreateUnmapped}
              size="small"
              aria-label="Create unmapped node"
              sx={{ color: extraColors.white }}
            >
              <AddIcon />
            </IconButton>
          )}
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close drawer"
            sx={{ color: extraColors.white }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto" }}>
        {nodeOptions.length === 0 && open && (
          <Typography
            variant="body2"
            sx={{ color: extraColors.hint, textAlign: "center", pt: 4 }}
          >
            {unmapped ? "No unmapped nodes" : "No nodes selected"}
          </Typography>
        )}
        {nodeOptions.map(p => (
          <NodeAccordion
            key={p.platform_id}
            node={p}
            sensors={sensorsByNode.get(p.platform_id) ?? []}
            operationMap={operationMap}
            unitMap={unitMap}
            contactMap={contactMap}
            unmapped={unmapped}
            onPlaceOnMap={
              unmapped && onPlaceOnMap
                ? () => onPlaceOnMap(p.platform_id)
                : undefined
            }
          />
        ))}
      </Box>
    </Drawer>
  )
}
